import { createElement, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardActionAreaProps,
  CardActions,
  Divider,
  Paper,
  Stack,
  styled,
  Typography,
} from '@mui/material';

import { ImageWithSkeleton, TitleTypeChip } from '@components/base';
import { TagGrid } from '@components/content';
import HighlightedTypography from '@components/layout/DetailPanel/HighlightedTypography';
import { EpisodeInfo, SeriesCountDisplay } from '@components/media';
import { IndexName } from '@enums/IndexName';
import useGlobalViewMode from '@hooks/useGlobalViewMode';
import useMetadataContext from '@hooks/useMetadataContext';
import { useProviderNames } from '@hooks/useProviderNames';
import { ImageType } from '@models/Image';
import { SimpleTitle, TTitleContext } from '@models/Title';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { getTitleImageUrlFromProvider } from '@utils/imageUtils';

export type SearchCardActionProps = {
  simpleTitle: SimpleTitle;
};

type SearchCardProps = {
  actions?: FC<SearchCardActionProps>;
  onClick?: (simpleTitle: SimpleTitle) => void;
  title: SimpleTitle;
};

const StyledCard = styled(Card)`
  transition: 150ms;
  padding: 0;
  display: flex;
  flex-direction: column;

  &.wide {
    min-height: 320px;
  }
`;

const StyledCardActionArea = styled(CardActionArea)`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  justify-content: flex-start;
  flex: 1;

  &.wide {
    flex-direction: column;
  }

  &.poster,
  &.square {
    flex-direction: row;
  }
`;

const StyledTitleTypeChip = styled(TitleTypeChip)`
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
  width: 100%;
`;

const imageWidthMap: Record<ImageType, number> = { poster: 120, square: 160, wide: 1 };

export const SearchResultCard: FC<SearchCardProps> = ({
  actions,
  onClick,
  title,
  title: { masterId, productionYear, series, providers, titleName, type },
}) => {
  const { t } = useTranslation();
  const { getTitleLinkProps } = useMetadataContext();
  const { imageType } = useGlobalViewMode();

  const { getProviderList } = useProviderNames();

  const {
    searchQuery: { indexName },
  } = useSearchAndFilter();

  const titleContext: TTitleContext = {
    masterId,
    providerIdentifier: indexName === IndexName.PlaybackOptions ? providers[0] : undefined,
  };

  const actionAreaProps: CardActionAreaProps = onClick
    ? { onClick: () => onClick(title) }
    : ({ ...getTitleLinkProps(titleContext), component: Link } as CardActionAreaProps);

  const imgWidth = imageWidthMap[imageType];
  const stacked = imgWidth === 1;

  return (
    <StyledCard className={imageType}>
      <StyledCardActionArea aria-label={titleName} className={imageType} {...actionAreaProps}>
        <Stack height={1} width={1}>
          <Stack direction={stacked ? 'column' : 'row'} height={1}>
            <Box
              maxWidth={imgWidth === 1 ? undefined : '33%'}
              p={1}
              pb={stacked ? 0 : undefined}
              pr={stacked ? undefined : 0}
              width={imgWidth}
            >
              <Box component={Paper} elevation={0} overflow='hidden'>
                <ImageWithSkeleton
                  alt={titleName}
                  key={imageType}
                  ratio={imageType}
                  sizes={imgWidth === 1 ? undefined : `${imgWidth}px`}
                  src={getTitleImageUrlFromProvider(
                    title,
                    imageType,
                    indexName === IndexName.PlaybackOptions && providers && providers.length > 0
                      ? providers[0]
                      : undefined,
                    { width: imgWidth === 1 ? 400 : imgWidth }
                  )}
                  style={{ display: 'flex', alignSelf: 'flex-start' }}
                />

                <Divider />

                <StyledTitleTypeChip titleType={type} variant='filled' />
              </Box>
            </Box>

            <Stack flex={1} justifyContent='space-between' overflow='hidden' p={1}>
              <Stack overflow='hidden'>
                <Typography color='textSecondary' lineHeight={1.25} variant='caption'>
                  {productionYear}
                </Typography>

                <HighlightedTypography
                  asset={title}
                  field='titleName'
                  title={t('titleName')}
                  variant='h4'
                />

                <HighlightedTypography
                  asset={title}
                  color='textSecondary'
                  field='originalTitle'
                  title={t('originalTitle')}
                  variant='caption'
                />

                {type === 'series' && <SeriesCountDisplay display='inline' series={series} />}
                {type === 'episode' && <EpisodeInfo display='inline' title={title} />}
              </Stack>

              <Stack justifyContent='flex-end'>
                <Typography lineHeight={2} mt={1} variant='h6'>
                  Providers
                </Typography>

                <TagGrid maxTags={5} softTags tags={getProviderList(providers)} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </StyledCardActionArea>

      {actions ? (
        <CardActions>{createElement(actions, { simpleTitle: title }, [])}</CardActions>
      ) : null}
    </StyledCard>
  );
};
